import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  TrendingUp, 
  Clock, 
  Filter,
  Grid,
  List,
  SortAsc,
  SortDesc,
  ExternalLink,
  Copy,
  Heart,
  Eye
} from 'lucide-react';
import NFTCard from '../components/NFTCard';

const Collection = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('newest');
  const [filterPrice, setFilterPrice] = useState('all');

  const collection = {
    name: "Supreme Collection",
    description: "The Supreme Collection represents the pinnacle of digital art on Supreme Chain. Each piece is carefully crafted by world-renowned artists and represents the future of blockchain-based creativity.",
    image: "https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?w=800&h=400&fit=crop",
    creator: {
      address: "0x1234567890123456789012345678901234567890",
      name: "SupremeArtist",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
    },
    stats: {
      items: 10000,
      owners: 2500,
      floorPrice: "0.1 ETH",
      volume: "2.4 ETH",
      listed: 150
    },
    verified: true
  };

  const nfts = [
    {
      id: 1,
      name: "Supreme Genesis #1",
      image: "https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?w=400&h=400&fit=crop",
      price: "0.1",
      currency: "ETH",
      creator: "0x1234...5678",
      likes: 42,
      views: 128
    },
    {
      id: 2,
      name: "Digital Crown #5",
      image: "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&h=400&fit=crop",
      price: "0.25",
      currency: "ETH",
      creator: "0x8765...4321",
      likes: 38,
      views: 95
    },
    {
      id: 3,
      name: "Royal Artifact #12",
      image: "https://images.unsplash.com/photo-1642790105077-9c5a4a5b5b5b?w=400&h=400&fit=crop",
      price: "0.5",
      currency: "SUP",
      creator: "0xabcd...efgh",
      likes: 67,
      views: 203
    },
    {
      id: 4,
      name: "Supreme Collection #8",
      image: "https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?w=400&h=400&fit=crop",
      price: "0.15",
      currency: "ETH",
      creator: "0x5678...9abc",
      likes: 29,
      views: 87
    },
    {
      id: 5,
      name: "Cosmic Warrior #23",
      image: "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&h=400&fit=crop",
      price: "0.8",
      currency: "ETH",
      creator: "0xdef0...1234",
      likes: 89,
      views: 312
    },
    {
      id: 6,
      name: "Neon Dreams #7",
      image: "https://images.unsplash.com/photo-1642790105077-9c5a4a5b5b5b?w=400&h=400&fit=crop",
      price: "0.3",
      currency: "SUP",
      creator: "0x9876...5432",
      likes: 156,
      views: 445
    }
  ];

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
  };

  return (
    <div className="min-h-screen py-8">
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="max-w-7xl mx-auto"
      >
        {/* Collection Header */}
        <div className="relative mb-8">
          <div className="h-64 rounded-2xl overflow-hidden">
            <img
              src={collection.image}
              alt={collection.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          </div>
          
          <div className="absolute bottom-6 left-6 right-6">
            <div className="flex flex-col md:flex-row items-start md:items-end gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-white">{collection.name}</h1>
                  {collection.verified && (
                    <div className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                      Verified
                    </div>
                  )}
                </div>
                <p className="text-gray-300 mb-4 max-w-2xl">{collection.description}</p>
                
                {/* Creator */}
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={collection.creator.avatar}
                    alt="Creator"
                    className="w-10 h-10 rounded-full"
                  />
                  <div>
                    <p className="text-white font-medium">Created by {collection.creator.name}</p>
                    <button
                      onClick={() => copyAddress(collection.creator.address)}
                      className="text-gray-400 text-sm hover:text-white flex items-center gap-1"
                    >
                      {collection.creator.address.slice(0, 6)}...{collection.creator.address.slice(-4)}
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-2">
                  <Heart className="h-4 w-4" />
                  Follow
                </button>
                <button className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8"
        >
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 text-center">
            <p className="text-2xl font-bold text-white">{collection.stats.items.toLocaleString()}</p>
            <p className="text-gray-400 text-sm">Items</p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 text-center">
            <p className="text-2xl font-bold text-white">{collection.stats.owners.toLocaleString()}</p>
            <p className="text-gray-400 text-sm">Owners</p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 text-center">
            <p className="text-2xl font-bold text-white">{collection.stats.floorPrice}</p>
            <p className="text-gray-400 text-sm">Floor Price</p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 text-center">
            <p className="text-2xl font-bold text-white">{collection.stats.volume}</p>
            <p className="text-gray-400 text-sm">Volume</p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 text-center">
            <p className="text-2xl font-bold text-white">{collection.stats.listed}</p>
            <p className="text-gray-400 text-sm">Listed</p>
          </div>
        </motion.div>

        {/* Filters and Controls */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 mb-8"
        >
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-4">
              <select
                value={filterPrice}
                onChange={(e) => setFilterPrice(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Prices</option>
                <option value="low">Under 0.2 ETH</option>
                <option value="medium">0.2 - 0.5 ETH</option>
                <option value="high">Over 0.5 ETH</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="newest">Newest</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="likes">Most Liked</option>
                <option value="views">Most Viewed</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex bg-white/10 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'grid' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'list' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* NFT Grid */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className={`grid gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
              : 'grid-cols-1'
          }`}
        >
          {nfts.map((nft, index) => (
            <motion.div
              key={nft.id}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 + index * 0.1 }}
            >
              <NFTCard nft={nft} />
            </motion.div>
          ))}
        </motion.div>

        {/* Load More */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-12"
        >
          <button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-200">
            Load More NFTs
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Collection;
